/**
 * The SDK's turn-0 string pack (FLO-531).
 *
 * Everything the agent SAYS is already server-driven and therefore already
 * speaks the session language. What isn't: the handful of strings the SDK
 * hardcodes for the moments AROUND the conversation — greeting bubbles, opener
 * chips, status copy, the intro form, the wrap-up. On a Hindi site those
 * rendered in English, so the visitor met an English widget wrapped around a
 * Hindi agent.
 *
 * This is deliberately NOT an i18n framework. floe-api ships every allowed
 * language and its `uiStrings` inside the surface's `localizationPolicies`
 * entry. The SDK selects a visitor locale before first render and may install a
 * different local pack after a live-session acknowledgement. Resolution is PER
 * KEY: a key the server omits falls back to English, so a partial pack degrades
 * one string at a time instead of rendering blanks.
 *
 * The store is module-level rather than React context because the intake runs
 * in `validateClientKeyAndBotStatus`, which completes before `initReactUI`
 * mounts anything — every reader is downstream of the write, and threading a
 * prop through five components would have been the entire diff.
 *
 * The SDK is deliberately single-instance per page: its UI also owns fixed DOM
 * ids and shared scroll/squeeze resources. `claimUiStrings` enforces that
 * existing constraint before validation can install a locale, so a second
 * `Floe()` fails clearly instead of silently changing the first widget's copy.
 * A teardown releases the claim, allowing a replacement instance to start.
 */

/**
 * Every language floe-api can put in a localization policy. This is the same
 * closed list as floe-api's `language.config.ts` and pipecat's language
 * registry (pipecat's suite fails if they disagree): a code outside it is
 * a malformed policy and the SDK refuses to mount on it.
 */
export type SupportedLanguage =
  | 'en'
  | 'hi'
  | 'es'
  | 'fr'
  | 'de'
  | 'pt'
  | 'it'
  | 'nl'
  | 'ru'
  | 'ja'
  | 'pl'
  | 'sv'
  | 'fi'
  | 'cs'
  | 'tr'
  | 'da'
  | 'no'
  | 'ta'
  | 'te'
  | 'bn'
  | 'mr';

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = [
  'en',
  'hi',
  'es',
  'fr',
  'de',
  'pt',
  'it',
  'nl',
  'ru',
  'ja',
  'pl',
  'sv',
  'fi',
  'cs',
  'tr',
  'da',
  'no',
  'ta',
  'te',
  'bn',
  'mr',
];

export function isSupportedLanguage(raw: unknown): raw is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(raw as SupportedLanguage);
}

/**
 * English defaults — the source of truth for BOTH the rendered English copy and
 * the key list floe-api translates. Editing a value here changes what English
 * visitors see; adding a key here is a request for a value in EVERY other
 * language floe-api serves (floe-api/src/sites/ui-string-packs/), and floe-api's
 * parity spec fails until each pack has one.
 *
 * `{name}` placeholders are filled by the caller (see getString). Keys marked
 * "additive" are ones this lane declared beyond the frozen contract list
 * because leaving them English would have left a half-translated surface.
 */
export const EN_STRINGS = {
  'identity.confirm': 'Confirm',
  'identity.reject': 'That is not right',
  'identity.required': 'Required for the walkthrough',
  'identity.placeholder.phone': 'Phone number, including country code',
  'identity.placeholder.email': 'you@company.com',
  'identity.placeholder.name': 'Your name',
  'identity.placeholder.company': 'Company',
  'identity.placeholder.role': 'Your role',
  'demo.introPhone': 'Phone number',
  'demo.introPhoneInvalid': 'Enter a valid phone number including its country code.',
  'demo.kickoff.phone': 'my phone number is {phone}',
  'identity.helper.phone.asked': 'You can type your phone number here.',
  'identity.helper.phone.proposed': 'Check your phone number, edit if needed, then press Enter.',
  'identity.helper.email.asked': 'You can type your email address here.',
  'identity.helper.email.proposed': 'Check your email address, edit if needed, then press Enter.',
  'identity.helper.name.asked': 'You can type your name here.',
  'identity.helper.name.proposed': 'Check your name, edit if needed, then press Enter.',
  'identity.helper.company.asked': 'You can type your company here.',
  'identity.helper.company.proposed': 'Check your company, edit if needed, then press Enter.',
  'identity.helper.role.asked': 'You can type your role here.',
  'identity.helper.role.proposed': 'Check your role, edit if needed, then press Enter.',

  // ── Website agent ────────────────────────────────────────────────────────
  'wa.greeting1': 'Hi 👋 — ask me anything about {name}.',
  'wa.greeting2':
    "I can answer questions and, when you're ready, show you a live demo — no form, no wait.",
  'wa.exitIntent.greeting1': 'Before you go — want to see it live?', // additive
  'wa.exitIntent.greeting2':
    'Ask a question or start a live demo. Nothing starts until you choose.', // additive
  'wa.placeholder': 'Ask {name} anything…',
  'wa.consent': 'Powered by Floe · conversations may be recorded',
  /** Divider over a restored transcript whose thread the server could NOT
   *  resume — the bot starts cold, and the painted history must say so. */
  'wa.freshStart': 'Earlier conversation shown for reference — starting fresh', // additive
  'wa.seeLive': 'See it live',
  /** Injected as a visible user bubble AND as LLM input — an English value here
   *  flips a Hindi conversation back to English mid-session. */
  'wa.demoRequest': 'Can I see a live demo?',
  'wa.status.connecting': 'Connecting…',
  'wa.status.listening': 'Listening…',
  'wa.status.thinking': 'Thinking…',
  'wa.status.replying': 'Replying…',
  'wa.status.online': 'Online',
  'wa.status.reconnecting': 'Reconnecting…',
  'wa.status.ended': 'Chat ended',
  'wa.status.dormant': 'Ready when you are', // additive
  'wa.status.guiding': 'Guiding…', // additive — onboarding-only
  'wa.status.paused': 'Paused', // additive — onboarding-only
  'wa.capture.title': 'Want a copy of this chat?',
  // "will", not the "’ll" the JSX used: `{name}` is the SITE name far more often
  // than the "we" fallback, and "Acme’ll follow up" is not English. This is the
  // one place the pack deliberately changes English copy rather than lifting it.
  'wa.capture.body':
    'Drop your details and {name} will follow up — optional, keep chatting either way.', // additive
  'wa.capture.submit': 'Submit',
  'wa.capture.dismiss': 'Not now',
  'wa.capture.emailLabel': 'Email', // additive
  'wa.capture.emailPlaceholder': 'you@company.com', // additive
  'wa.capture.nameLabel': 'Name', // additive
  'wa.capture.namePlaceholder': 'Your name', // additive
  'wa.capture.companyLabel': 'Company', // additive
  'wa.capture.companyPlaceholder': 'Company', // additive
  'wa.ended.title': 'Chat ended',
  'wa.ended.body': 'Thanks for stopping by. Have another question about {name}?',
  'wa.ended.again': 'Ask another question',
  'wa.opener1': 'What does {name} do?',
  'wa.opener2': 'How does pricing work?',
  'wa.opener3': 'Can I see it in action?',
  'wa.showLiveDemo': 'Show live demo', // additive — dynamic opener fallback
  /** The mic opt-in CTA under the avatar hero. `{name}` falls back to "us". */
  'wa.speakWith': 'Speak with {name}', // additive
  'wa.demoComposer': 'Ask or steer the demo…', // additive (FLO-514 demo-focus composer)
  'wa.talkWith': 'Talk with {name}', // additive (FLO-514 video-tile launcher)
  'wa.talkToAgent': 'Talk to agent', // additive — expanded voice-first launcher
  'wa.returnToCall': 'Return to call', // additive — minimized live session
  // FLO-754 — the avatar compact dock. A face on the page makes the third
  // person strange, so the resting pill speaks as the presenter; the pill
  // launcher (no face) keeps 'wa.talkToAgent'/'Ask {name}'.
  'wa.talkToMe': 'Talk to me', // additive — resting pill, avatar present
  'wa.typeInstead': 'Type instead', // additive — avatar dock, bar → composer
  'wa.moreControls': 'More controls', // additive — avatar dock, bar opens
  'wa.fewerControls': 'Fewer controls', // additive — avatar dock, bar closes
  'wa.backToVoice': 'Back to voice controls', // additive — avatar dock, leave typing
  'wa.earlierTurns': 'Earlier', // additive — avatar dock, expand the bubble
  'wa.backToLive': 'Back to live', // additive — avatar dock, collapse the bubble
  'wa.followUpPlaceholder': 'Ask a follow-up', // additive — avatar dock composer
  'wa.youLabel': 'You', // additive — avatar dock scrollback speaker label
  'wa.activeCallControls': 'Active call controls', // additive — minimized live session
  'wa.ask': 'Ask {name}', // additive (FLO-514 compact launcher)
  'wa.askAnything': 'Ask {name} anything', // additive — compact launcher aria
  'wa.askQuestion': 'Ask a question', // additive — unnamed launcher aria
  // WebsiteAgentChat controls + booking/end states. These are assistive labels
  // as well as visible copy, so a translated card cannot leave screen-reader
  // controls in English.
  'wa.dialogLabel': 'Chat with {name}', // additive
  'wa.bookCall': 'Book a call', // additive
  'wa.micBlocked':
    'Microphone blocked — allow it in your browser settings, or tap to ask again', // additive
  // FLO-786 retired `wa.micBlockedHint`, `wa.micUnavailableHint` and
  // `wa.micInviteRetry` from the rendered surface (the blocked state is a
  // notice now, not a retry card). `wa.micBlocked` / `wa.micUnavailable` below
  // are still live — they label the mic control itself. The retired three stay
  // in the pack because
  // REMOVING a key has the same failure mode as renaming one: a deployed SDK —
  // CDN-cached, or an npm version a customer pinned — still asks for it, and a
  // server that stopped shipping it silently reverts that string to English for
  // Hindi visitors. Drop them once no released bundle renders them.
  'wa.micBlockedHint': 'Mic blocked · Tap to retry', // additive
  'wa.micUnavailable':
    'Microphone unavailable — check your device or type instead', // additive
  'wa.micUnavailableHint': 'Mic unavailable · Tap to retry', // additive
  // FLO-786: the blocked/unavailable states are NOTICES, not alarms. The visitor
  // chose to block the mic (or has no device); the composer never stopped
  // working, and a permission change is picked up live, so there is nothing to
  // instruct and nothing to retry here. Deliberately no address-bar directions:
  // true on desktop, false on iOS Safari, and a wrong instruction is worse than
  // none. The mic control keeps the interactive retry.
  'wa.micBlockedNotice':
    'Your browser is blocking the microphone for this site. You can keep typing.', // additive
  'wa.micUnavailableNotice':
    'No microphone available here. You can keep typing.', // additive
  'wa.micInviteTitle': 'Ready to talk?', // additive — shown once after the spoken welcome
  'wa.micInviteAction': 'Turn on mic', // additive — explicit permission gesture
  'wa.micInviteRetry': 'Try mic again', // additive — denied/unavailable recovery
  'wa.micInviteDismiss': 'Keep typing', // additive — dismiss the one-time invitation
  'wa.micListening': 'Microphone on — listening', // additive
  'wa.micDisable': 'Turn off microphone', // additive
  'wa.micEnable': 'Speak — turn on microphone', // additive
  'wa.micMuteHint': 'Mic on — tap to mute', // additive
  'wa.voiceRepliesEnable': 'Turn on voice replies', // additive
  'wa.voiceRepliesMute': 'Mute voice replies', // additive
  'wa.useKeyboard': 'Use keyboard and mute voice replies', // additive
  'wa.minimize': 'Minimize', // additive
  'wa.endChat': 'End chat', // additive
  'wa.booking.back': 'Back to the conversation', // additive
  'wa.booking.close': 'Close', // additive — the booking stage's cross
  'wa.booking.openNewTab': 'Open in a new tab ↗', // additive
  'wa.booking.title': 'Grab a time with the team', // additive
  // No "this chat stays right here": after End the stage opens with no chat.
  'wa.booking.body': 'The booking page opens in a new tab.', // additive
  'wa.booking.beforeTitle': 'Before you go', // additive
  'wa.booking.beforeBody': 'Want to put time with the {name} team on the calendar?', // additive
  'wa.booking.keepChatting': 'Keep chatting', // additive
  'wa.send': 'Send', // additive

  // ── Shared language + connection chrome ─────────────────────────────────
  'language.label': 'Language',
  'language.switching': 'Switching…',
  'language.switchFailed': "Couldn’t switch language. Try again.",
  'connection.initializing': 'Initializing…',
  'connection.authenticating': 'Authenticating…',
  'connection.connecting': 'Connecting to server…',
  'connection.joining': 'Joining session…',
  'connection.ready': 'Connected',
  'connection.failed': 'Connection failed',

  // ── Demo overlay ─────────────────────────────────────────────────────────
  // The state pill spells its ellipsis with three dots while the composer
  // placeholder uses U+2026; both are kept verbatim (demo.state.connecting vs
  // demo.connecting) so English stays byte-identical to today.
  'demo.state.connecting': 'Connecting...',
  'demo.state.listening': 'Listening',
  'demo.state.thinking': 'Thinking',
  'demo.state.speaking': 'Speaking',
  'demo.state.guiding': 'Guiding', // additive
  'demo.state.ready': 'Ready', // additive
  'demo.connecting': 'Connecting…', // additive — composer placeholder
  'demo.waiting': 'Waiting for conversation…', // additive — empty transcript
  // The intro form, in render order. `demo.emailPrompt` is the stage-1
  // HEADLINE and introEmail/introName are FIELD LABELS — that split is what the
  // shipped hi pack's copy was written against (floe-api ui-strings.ts), and
  // the labels have to stay short because they sit above the inputs.
  'demo.emailPrompt': "What's your work email?",
  'demo.introEmailBody': "We'll use it to personalize your live demo. Takes about ten seconds.", // additive
  'demo.introEmail': 'Work email',
  'demo.introMoreTitle': 'One more thing.', // additive — stage-2 headline
  'demo.introNameBody':
    'Your name lets the agent address you. The rest helps tailor the demo to your role.', // additive
  'demo.introName': 'Your name',
  'demo.introOptional': 'Optional', // additive
  'demo.introOptionalBody': 'Helps the agent tailor what to show you.', // additive
  'demo.introCompany': 'Company', // additive
  'demo.introRole': 'Your role', // additive
  'demo.introContinue': 'Continue', // additive
  'demo.introStart': 'Start demo', // additive
  'demo.pillEmail': 'Enter your work email for a tailored demo', // additive — docked pill
  // additive — obvious-fake refusal under the intro form and the docked pill
  'demo.introEmailRefused': 'Please enter the email address you actually use.',
  'demo.introThanks': 'Thanks, {name}.', // additive — submitted-form card
  'demo.starting': 'Starting your personalized demo…',
  'demo.ended': 'Demo session ended',
  'demo.error': 'The demo session ended unexpectedly.',
  'demo.errorUnreachable': "Couldn't reach {name}. Try again, or book a call.", // additive
  'demo.tryAgain': 'Try again',
  'demo.typeMessage': 'Type a message…',
  'demo.close': 'Close demo',
  'demo.wrapTitle': 'Wrap up the demo?',
  // Two bodies: the booking CTA only renders when the site has a calendar URL.
  // `demo.wrapBody` is the WITH-calendar copy (what the hi pack translated).
  'demo.wrapBody': 'Book a demo to dig into your use case with our team, or end the call.',
  'demo.wrapNoBookBody': 'Ready to wrap up? You can end the call below.', // additive
  'demo.endCall': 'End Call',
  'demo.you': 'You',
  'demo.bot': 'Floe',
  // Chrome the prospect reads while the demo is running or wrapping up. These
  // sit next to already-translated copy, so leaving them English is exactly the
  // half-translated surface the pack exists to prevent.
  'demo.liveDemo': 'Live demo', // additive — header subtitle + "live" chips
  'demo.liveBadge': 'Live', // additive — the canvas corner badge
  // The ONE line on the website→demo hand-over card (see sdk/demo-handover.ts).
  // Declarative and in the present tense: it states what is happening, never
  // that anything is loading, waiting or being prepared — a visitor told to wait
  // starts counting, and the sentence would also be a lie the moment a
  // navigation stalls. Deliberately says "product", not "demo": the prospect is
  // about to look at the real application, which is the whole claim.
  'demo.opening': 'Opening the live product', // additive — hand-over stage card
  'demo.maximize': 'Maximize', // additive — website-agent demo controls
  'demo.restore': 'Restore', // additive — website-agent demo controls
  'demo.exitFullscreen': 'Exit full screen', // additive — website-agent demo controls
  'demo.hideChat': 'Hide chat', // additive — demo sidebar control
  'demo.nextSteps': 'Ready to talk next steps?', // additive — post-demo CTA
  'demo.recapTitle': 'What we covered', // additive — recap section heading
  'demo.nextLabel': 'Next', // additive — recap "next step" eyebrow
  'demo.bookCall': 'Book a call', // additive
  'demo.bookDemo': 'Book Demo', // additive
  'demo.bookLiveDemo': 'Book a live demo', // additive
  // Safe fallback when an old/untagged deterministic starter row cannot prove
  // it belongs to the active language.
  'demo.starter1': 'Show me the product', // additive
  'demo.starter2': 'How would this help my team?', // additive
  'demo.starter3': "I'm just exploring", // additive

  // ── Demo intro-form kickoff sentence ─────────────────────────────────────
  // Composed clause-by-clause (company/role are optional, so a single-sentence
  // template would leave dangling fragments). English joins with ", " and ends
  // with demo.kickoff.end — Hindi swaps that terminator for a danda.
  'demo.kickoff.name': 'My name is {name}', // additive
  'demo.kickoff.email': 'my email is {email}', // additive
  'demo.kickoff.company': 'I work at {company}', // additive
  'demo.kickoff.role': "I'm a {role}", // additive
  'demo.kickoff.end': '.', // additive
} as const;

export type UiStringKey = keyof typeof EN_STRINGS;

const KNOWN_KEYS = new Set<string>(Object.keys(EN_STRINGS));

let language: SupportedLanguage = 'en';
let overrides: Partial<Record<UiStringKey, string>> = {};
let owner: object | null = null;

function resetValues(): void {
  language = 'en';
  overrides = {};
}

/**
 * Reserve the module-level pack for one active SDK. The rendered SDK already
 * has page-global ownership (fixed mount ids, scroll lock, squeeze state), so
 * rejecting a concurrent owner is both safer and more honest than allowing it
 * to replace the language read by an existing tree.
 */
export function claimUiStrings(candidate: object): void {
  if (owner && owner !== candidate) {
    throw new Error(
      '[OnboardingSDK] Only one active Floe instance is supported per page. Disconnect the existing instance before starting another.',
    );
  }
  if (!owner) resetValues();
  owner = candidate;
}

/** Release a claim held by a torn-down or rejected SDK instance. */
export function releaseUiStrings(candidate: object): void {
  if (owner !== candidate) return;
  owner = null;
  resetValues();
}

/** Install a language that has already passed the authoritative policy parser. */
export function setLanguage(nextLanguage: SupportedLanguage): SupportedLanguage {
  language = nextLanguage;
  return language;
}

export function getLanguage(): SupportedLanguage {
  return language;
}

/**
 * Install a server-supplied pack, REPLACING any previous one. Unknown keys and
 * non-string/blank values are dropped rather than stored: a stray key can't
 * shadow a default, and a blank one can't render an empty label. Returns how
 * many keys were accepted so the caller can log the pack size.
 */
export function setUiStrings(raw: unknown): number {
  overrides = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return 0;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!KNOWN_KEYS.has(key)) continue;
    if (typeof value !== 'string' || !value.trim()) continue;
    overrides[key as UiStringKey] = value;
  }
  return Object.keys(overrides).length;
}

/** Reset to English defaults and clear ownership — used by tests. */
export function resetUiStrings(): void {
  owner = null;
  resetValues();
}

/**
 * Resolve one string: server pack → English default, then fill `{placeholder}`
 * tokens. A placeholder with no matching var is left intact rather than blanked,
 * so a mismatched pack reads as a visible defect instead of a truncated sentence.
 */
export function getString(key: UiStringKey, vars?: Record<string, string | undefined>): string {
  const template = overrides[key] ?? EN_STRINGS[key];
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, token: string) => vars[token] ?? match);
}

/**
 * Resolve a string whose `{name}` is the site name, for a site that may not
 * have one.
 *
 * English keeps the wording it always had ("Speak with us", "Couldn't reach the
 * product"). No other language can: an English noun dropped into a translated
 * sentence reads as a bug ("us से बात कीजिए"), and it cannot be declined or
 * given an article either. Those fall back to a string that needs no name.
 */
export const getNamedString = (
  key: UiStringKey,
  name: string | undefined,
  englishFallbackName: string,
  namelessKey: UiStringKey,
): string => {
  if (name) {
    return getString(key, { name });
  }
  return language === 'en'
    ? getString(key, { name: englishFallbackName })
    : getString(namelessKey);
};
